import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Icon } from "../components/Icon";
import { LanguageToggle } from "../components/LanguageToggle";
import { ep } from "../api/endpoints";
import { useT } from "../i18n/strings";
import { pick } from "../i18n/labels";
import { fieldLabels, type FieldLabel } from "../i18n/fieldLabels";
import {
  clarityLabels,
  cutLabels,
  kindLabels,
  labelOf,
  metalColorLabels,
  metalLabels,
  purityLabel,
  stoneShapeLabels,
  stoneTypeLabels,
} from "../i18n/choices";
import { formatGrams, formatNumber, formatTime, formatToman, isLatinScript } from "../i18n/format";
import type { PublicMedia, PublicPiece as Piece } from "../api/types";
import d from "../features/pieces/detail.module.css";
import shell from "../components/AppShell.module.css";
import p from "./public.module.css";
import { PhotoViewer } from "./PhotoViewer";

const BASE = (import.meta.env.VITE_API_BASE ?? "/api/v1").replace(/\/$/, "");

type State =
  | { kind: "loading" }
  | { kind: "ok"; piece: Piece }
  | { kind: "gone" }
  | { kind: "error"; throttled: boolean };

function usePublicPiece(code: string | null, attempt: number): State {
  const [state, setState] = useState<State>({ kind: "loading" });
  useEffect(() => {
    if (!code) {
      setState({ kind: "gone" });
      return;
    }
    const abort = new AbortController();
    setState({ kind: "loading" });
    fetch(`${BASE}${ep.publicPiece(code)}`, { signal: abort.signal })
      .then(async (response) => {
        if (response.ok) setState({ kind: "ok", piece: (await response.json()) as Piece });
        else if (response.status === 404) setState({ kind: "gone" });
        else setState({ kind: "error", throttled: response.status === 429 });
      })
      .catch((error: Error) => {
        if (error.name !== "AbortError") setState({ kind: "error", throttled: false });
      });
    return () => abort.abort();
  }, [code, attempt]);
  return state;
}

export function PublicPiece({ code }: { code: string | null }) {
  const t = useT();
  const [attempt, setAttempt] = useState(0);
  const state = usePublicPiece(code, attempt);
  const shop = (state.kind === "ok" && state.piece.shop_name) || t.appName;

  useEffect(() => {
    document.title = state.kind === "ok" ? `${state.piece.title} · ${shop}` : shop;
  }, [state, shop]);

  return (
    <div className={p.page}>
      <header className={p.bar}>
        <span className={p.shop}>
          <Icon name="shop" size={20} />
          <span className="bidi">{shop}</span>
        </span>
        <LanguageToggle />
      </header>

      {state.kind === "loading" && <Loading />}
      {state.kind === "gone" && (
        <Message icon="qr" title={t.publicPage.notFound} body={t.publicPage.notFoundBody} />
      )}
      {state.kind === "error" && (
        <Message
          icon="alert"
          title={t.errors.title}
          body={state.throttled ? t.errors.throttled(30) : t.errors.network}
          action={
            <button type="button" className={p.retry} onClick={() => setAttempt((n) => n + 1)}>
              <Icon name="refresh" size={16} />
              {t.publicPage.retry}
            </button>
          }
        />
      )}
      {state.kind === "ok" && <Sheet piece={state.piece} />}
    </div>
  );
}

function Sheet({ piece }: { piece: Piece }) {
  const t = useT();
  const [index, setIndex] = useState(0);
  const [viewing, setViewing] = useState(false);
  const shown = piece.media[index] ?? piece.media[0];
  const photos = piece.media.filter((file) => file.mime_type.startsWith("image/"));
  const closeViewer = useCallback(() => setViewing(false), []);
  const followViewer = useCallback(
    (at: number) => setIndex(Math.max(0, piece.media.indexOf(photos[at]))),
    [piece.media, photos],
  );
  const latin = isLatinScript(piece.title);
  const subtitle = [
    labelOf(kindLabels, piece.kind),
    purityLabel(piece.purity_permille, piece.metal),
  ].filter(Boolean);
  const grams = (value: number | null) =>
    value === null ? null : <Measure value={formatGrams(value)} unit={t.form.gram} />;
  const pricing = piece.pricing;

  return (
    <main>
      <Hero media={shown} title={piece.title} onOpen={() => setViewing(true)} />
      {viewing && shown && photos.includes(shown) && (
        <PhotoViewer
          images={photos}
          start={photos.indexOf(shown)}
          title={piece.title}
          onIndex={followViewer}
          onClose={closeViewer}
        />
      )}

      {piece.media.length > 1 && (
        <div className={d.strip}>
          {piece.media.map((file, i) => (
            <button
              key={file.url}
              type="button"
              className={[d.stripTile, i === index && d.stripTileOn].filter(Boolean).join(" ")}
              aria-pressed={i === index}
              aria-label={t.publicPage.gallery(i + 1, piece.media.length)}
              onClick={() => setIndex(i)}
            >
              {file.thumbnail_url || file.mime_type.startsWith("image/") ? (
                <img
                  className={d.stripPreview}
                  src={file.thumbnail_url ?? file.display_url ?? file.url}
                  alt=""
                  loading="lazy"
                />
              ) : (
                <Icon name="video" size={20} />
              )}
            </button>
          ))}
        </div>
      )}

      <div className={d.titleBlock}>
        <h1 className={[d.title, latin && d.titleLatin, "bidi"].filter(Boolean).join(" ")}>
          {piece.title}
        </h1>
        {subtitle.length > 0 && (
          <p className={d.subtitle}>
            {subtitle.map((part, i) => (
              <span key={part} style={{ display: "contents" }}>
                {i > 0 && <span className={d.dot} />}
                <span className={i === 0 ? "bidi" : "mono"}>{part}</span>
              </span>
            ))}
          </p>
        )}
        {piece.availability && (
          <span className={`${d.statusChip} paper`} data-availability={piece.availability}>
            <span className={`${d.statusDot} ${p.dot}`} />
            {t.publicPage.availability[piece.availability]}
          </span>
        )}
      </div>

      <div className={p.content}>
        {pricing && (
          <Block title={t.publicPage.price} icon="valuation">
            <div className={d.valuationStack}>
              <div className={d.valuation}>
                {pricing.metal_rial > 0 && (
                  <div className={d.valuationRow}>
                    <span className={d.valuationLabel}>{t.detail.metalValue}</span>
                    <Toman rial={pricing.metal_rial} />
                  </div>
                )}
                {pricing.making_rial > 0 && pricing.making_percent !== null && (
                  <div className={d.valuationRow}>
                    <span className={d.valuationLabel}>
                      {t.detail.withMaking(formatNumber(pricing.making_percent))}
                    </span>
                    <Toman rial={pricing.making_rial} />
                  </div>
                )}
                {pricing.stones_rial !== null && (
                  <div className={d.valuationRow}>
                    <span className={d.valuationLabel}>{t.detail.stonesValue}</span>
                    <Toman rial={pricing.stones_rial} />
                  </div>
                )}
                <div className={`${d.valuationRow} ${d.valuationTotal}`}>
                  <span className={d.valuationLabel}>{t.publicPage.estimate}</span>
                  <Toman rial={pricing.total_rial} />
                </div>
              </div>
              <p className={d.evidenceNote}>
                <Icon name="info" size={16} />
                <span>
                  {t.publicPage.vatNote}{" "}
                  {pricing.as_of && `${t.prices.asOf(formatTime(pricing.as_of))} · `}
                  {t.prices.source}. {t.prices.disclaimer}
                </span>
              </p>
            </div>
          </Block>
        )}

        <Block title={t.publicPage.specs} icon="metal">
          <div className={d.rows}>
            <Row label={fieldLabels.metal}>{labelOf(metalLabels, piece.metal)}</Row>
            <Row label={fieldLabels.metal_color}>
              {labelOf(metalColorLabels, piece.metal_color)}
            </Row>
            <Row label={fieldLabels.purity_permille}>
              {piece.purity_permille !== null && (
                <span className="mono">{purityLabel(piece.purity_permille, piece.metal)}</span>
              )}
            </Row>
            <Row label={fieldLabels.gross_weight_g}>{grams(piece.gross_weight_g)}</Row>
            <Row label={fieldLabels.net_weight_g}>{grams(piece.net_weight_g)}</Row>
            <Row label={fieldLabels.size_label}>
              {piece.size_label && <span className="bidi">{piece.size_label}</span>}
            </Row>
            <Row label={fieldLabels.maker}>
              {piece.maker && <span className="bidi">{piece.maker}</span>}
            </Row>
          </div>
        </Block>

        {piece.stones.length > 0 && (
          <Block title={t.form.sections.stones} icon="stone">
            <div className={d.rows}>
              {piece.stones.map((stone, i) => {
                const head = [
                  labelOf(stoneTypeLabels, stone.stone_type),
                  labelOf(stoneShapeLabels, stone.shape),
                ]
                  .filter(Boolean)
                  .join(" · ");
                const grades = [
                  stone.carat !== null ? t.detail.carat(formatNumber(stone.carat)) : "",
                  stone.color,
                  labelOf(clarityLabels, stone.clarity),
                  labelOf(cutLabels, stone.cut),
                ].filter(Boolean);
                return (
                  <div key={i} className={d.row}>
                    <span className={d.rowLabel}>
                      {head} <span className="mono">{t.detail.stoneCount(stone.count)}</span>
                    </span>
                    <span className={d.rowValue}>
                      {grades.length > 0 && <span className="bidi">{grades.join(" · ")}</span>}
                    </span>
                  </div>
                );
              })}
            </div>
          </Block>
        )}
      </div>
    </main>
  );
}

function Hero({
  media,
  title,
  onOpen,
}: {
  media: PublicMedia | undefined;
  title: string;
  onOpen: () => void;
}) {
  const t = useT();
  return (
    <div className={d.hero}>
      <div className={d.heroFrame}>
        {!media ? (
          <span className={p.noMedia}>
            <Icon name="noImage" size={32} />
            {t.detail.noMedia}
          </span>
        ) : media.mime_type.startsWith("video/") ? (
          <video
            key={media.url}
            className={d.heroVideo}
            src={media.url}
            poster={media.display_url ?? undefined}
            controls
            playsInline
            preload="metadata"
          />
        ) : (
          <button
            type="button"
            className={p.heroOpen}
            onClick={onOpen}
            aria-label={t.publicPage.viewPhoto}
          >
            <img
              key={media.url}
              className={d.heroMedia}
              src={media.display_url ?? media.url}
              alt={title}
              decoding="async"
            />
            <span className={p.zoomBadge} aria-hidden>
              <Icon name="zoomIn" size={20} />
            </span>
          </button>
        )}
      </div>
    </div>
  );
}

/** The Mini App's section look, without pulling its router-bound shell in. */
function Block({
  title,
  icon,
  children,
}: {
  title: string;
  icon: "valuation" | "metal" | "stone";
  children: ReactNode;
}) {
  return (
    <section className={shell.section}>
      <div className={shell.sectionHead}>
        <Icon name={icon} size={16} />
        <h2 className="section-label">{title}</h2>
        <span className={shell.sectionRule} aria-hidden />
      </div>
      <div className={`${shell.sectionCard} paper`}>{children}</div>
    </section>
  );
}

function Row({ label, children }: { label: FieldLabel; children: ReactNode }) {
  if (children === null || children === undefined || children === false || children === "")
    return null;
  return (
    <div className={d.row}>
      <span className={d.rowLabel}>{pick(label)}</span>
      <span className={d.rowValue}>{children}</span>
    </div>
  );
}

/** Only the digits run LTR, so the unit lands on the reading side in both scripts. */
function Toman({ rial }: { rial: number }) {
  const t = useT();
  return (
    <span style={{ whiteSpace: "nowrap" }}>
      <span className={d.valuationAmount}>{formatToman(rial)}</span>{" "}
      <span className={d.rowValueMuted}>{t.prices.toman}</span>
    </span>
  );
}

function Measure({ value, unit }: { value: string; unit: string }) {
  return (
    <span>
      <span className="mono">{value}</span> <span className={d.rowValueMuted}>{unit}</span>
    </span>
  );
}

function Loading() {
  return (
    <div className={p.loading} aria-busy="true">
      <div className={p.skeletonHero} />
      <div className={p.skeletonLine} style={{ inlineSize: "70%" }} />
      <div className={p.skeletonLine} style={{ inlineSize: "40%" }} />
    </div>
  );
}

function Message({
  icon,
  title,
  body,
  action,
}: {
  icon: "qr" | "alert";
  title: string;
  body: string;
  action?: ReactNode;
}) {
  return (
    <div className={p.message}>
      <span className={p.messageMark}>
        <Icon name={icon} size={32} />
      </span>
      <h1 className={p.messageTitle}>{title}</h1>
      <p className={p.messageBody}>{body}</p>
      {action}
    </div>
  );
}
